import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  BadgeCheck, NotebookPen, Users, Award, Share2, 
  Brain, ClipboardList, ChevronLeft, ChevronRight, CheckCircle2 
} from "lucide-react";
import axios from "axios";
import { 
  HiMenuAlt3, HiX, HiChevronDown, HiOutlineViewGrid, 
  HiOutlineAcademicCap, HiOutlineCreditCard, HiOutlineStar, 
  HiOutlineChatAlt2, HiOutlineBell, HiOutlineUserCircle, HiOutlineLogout 
} from "react-icons/hi";

import { useAuth } from "../context/studentAuthContext";
import Logo from "../assets/images/peer2learn.png";
import { navigateToPeerMate } from "../utils/peermateHandoff";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Navbar() {
  const { student } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("main");
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const planName = subscriptionActive
    ? (subscriptionInfo?.plan_name || "Peermate")
    : "Free Plan";

  const planTheme = String(subscriptionInfo?.plan_theme_color || (subscriptionActive ? "blue" : "emerald")).toLowerCase();

  const planThemeClasses = {
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    red: "border-rose-200 bg-rose-50 text-rose-700",
    orange: "border-amber-200 bg-amber-50 text-amber-700",
    gold: "border-yellow-200 bg-yellow-50 text-yellow-700",
    purple: "border-violet-200 bg-violet-50 text-violet-700",
    pink: "border-pink-200 bg-pink-50 text-pink-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };

  const planBadgeClass = planThemeClasses[planTheme] || planThemeClasses.blue;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [mobileOpen]);

  useEffect(() => {
    if (!profileOpen) {
      setTimeout(() => setActiveMenu("main"), 300);
    }
  }, [profileOpen]);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!student?.id) {
        setSubscriptionActive(false);
        return;
      }
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/students/subscriptions/me/${student.id}`
        );
        const payload = res.data?.data || {};
        const isActive = payload?.isActive ?? res.data?.isActive;
        setSubscriptionActive(Boolean(isActive));
        setSubscriptionInfo(payload?.subscription || null);
      } catch (error) {
        console.error("Error fetching subscription:", error);
        setSubscriptionActive(false);
        setSubscriptionInfo(null);
      }
    };

    fetchSubscriptionStatus();
  }, [student?.id]);

  const isActive = (path) => location.pathname === path;

  const publicLinks = [
    { label: "Home", path: "/" },
    { label: "PeerMate AI", path: "/Peermatehomepage", isPeerMate: true },
    { label: "Contact Us", path: "/contact-us" },
    { label: "FAQs", path: "/faqs" },
  ];

  const dashboardLinks = [
    { label: "Dashboard", path: "/dashboard", icon: <HiOutlineViewGrid /> },
    { label: "PeerMate AI", path: "/Peermatehomepage", icon: <Brain />, isPeerMate: true },
    { label: "Journal", path: "/Journal", icon: <NotebookPen /> },
    { label: "Recommendations", path: "/Courserecommendation", icon: <Award /> },
    { label: "Certification", path: "/Certification", icon: <BadgeCheck /> },
    { label: "Marketplace", path: "/marketplace", icon: <ClipboardList />, comingSoon: true },
    { label: "Profile", path: "/profile", icon: <Users /> },
  ];

  const classesLinks = [
    { label: "My Classes", path: "/my-classes", icon: <HiOutlineAcademicCap /> },
    { label: "Group Classes", path: "/GroupClasses", icon: <Users /> },
    { label: "Assignments", path: "/assignments", icon: <ClipboardList /> },
  ];

  const activitiesLinks = [
    { label: "Payments", path: "/payment", icon: <HiOutlineCreditCard /> },
    { label: "Reviews", path: "/reviews", icon: <HiOutlineStar /> },
    { label: "Messages", path: "/messages", icon: <HiOutlineChatAlt2 /> },
    { label: "Notifications", path: "/notifications", icon: <HiOutlineBell /> },
    { label: "Referral", path: "/Referal", icon: <Share2 /> },
  ];

  const menuVariants = {
    initial: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
    animate: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction > 0 ? -100 : 100, opacity: 0 }),
  };

  const closeAll = () => {
    setMobileOpen(false);
    setProfileOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 px-4 md:px-6 ${
        scrolled ? "py-3 bg-white/95 backdrop-blur-md border-b border-slate-200/50 shadow-sm" : "py-6 bg-transparent"
      }`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">

          {/* Logo */}
          <Link to="/" onClick={closeAll} className="relative z-[70]">
            <motion.img 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              src={Logo} 
              alt="Peer2Learn" 
              className={`transition-all duration-300 object-contain ${scrolled ? "h-8 md:h-10" : "h-10 md:h-14"}`}
            />
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {publicLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (link.isPeerMate) {
                    navigateToPeerMate();
                  } else {
                    navigate(link.path);
                  }
                }}
                className={`text-sm font-semibold transition-colors duration-300 hover:text-[#1976D2] ${
                  isActive(link.path) ? "text-[#1976D2]" : "text-slate-800"
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {student ? (
              <div className="relative flex items-center gap-2">
                <div className={`hidden lg:flex items-center gap-1 rounded-full border px-2.5 py-1 ${planBadgeClass}`}>
                  <CheckCircle2 size={14} />
                  <span className="text-[11px] font-semibold">{planName}</span>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 p-1 bg-white border border-slate-200 rounded-full shadow-sm pr-3"
                >
                  <div className="w-8 h-8 rounded-full bg-[#1976D2] flex items-center justify-center text-white text-xs font-bold ring-2 ring-white">
                    {student.name?.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-slate-700 hidden lg:block">
                    {student.name?.split(" ")[0]}
                  </span>
                  <HiChevronDown className={`transition-transform duration-300 ${profileOpen ? "rotate-180" : ""}`} />
                </motion.button>

                {/* Profile Dropdown */}
                <AnimatePresence>
                  {profileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 15, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 15, scale: 0.95 }}
                      className="absolute top-full right-0 mt-3 w-72 bg-white border border-slate-200 rounded-2xl shadow-2xl p-2 z-[70] overflow-hidden"
                    >
                      <AnimatePresence mode="wait">
                        {activeMenu === "main" ? (
                          <motion.div key="main" variants={menuVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }}>
                            <div className="px-4 py-3 border-b border-slate-50 mb-2">
                              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest text-center">Account Information</p>
                              <p className="text-sm font-semibold text-slate-800 truncate text-center mt-1">{student.email}</p>
                            </div>
                              <div className={`flex items-center justify-center gap-2 px-4 py-2 mb-2 rounded-xl border ${planBadgeClass}`}>
                                <CheckCircle2 size={16} />
                                <span className="text-xs font-semibold">{planName}</span>
                              </div>
                            {dashboardLinks.map((item, idx) => (
                              item.comingSoon ? (
                                <div key={idx} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-slate-400 bg-slate-50 rounded-xl cursor-not-allowed">
                                  <div className="flex items-center gap-3">
                                    <span className="text-lg opacity-70">{item.icon}</span>
                                    <span>{item.label}</span>
                                  </div>
                                  <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">Coming Soon</span>
                                </div>
                              ) : item.isPeerMate ? (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => {
                                    closeAll();
                                    navigateToPeerMate();
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-[#1976D2] rounded-xl transition-all text-left"
                                >
                                  <span className="text-lg opacity-70">{item.icon}</span> {item.label}
                                </button>
                              ) : (
                                <Link key={idx} to={item.path} onClick={closeAll} className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-[#1976D2] rounded-xl transition-all">
                                  <span className="text-lg opacity-70">{item.icon}</span> {item.label}
                                </Link>
                              )
                            ))}
                            <div className="h-[1px] bg-slate-100 my-2 mx-2" />
                            <button onClick={() => setActiveMenu("classes")} className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 rounded-xl">
                              <div className="flex items-center gap-3"><HiOutlineAcademicCap className="text-lg opacity-70" /> Classes</div>
                              <ChevronRight size={16} />
                            </button>
                            <button onClick={() => setActiveMenu("activities")} className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 rounded-xl">
                              <div className="flex items-center gap-3"><HiOutlineCreditCard className="text-lg opacity-70" /> Activities</div>
                              <ChevronRight size={16} />
                            </button>
                            <button onClick={() => { navigate("/logout"); closeAll(); }} className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-sm text-red-500 hover:bg-red-50 rounded-xl border-t border-slate-50 font-semibold">
                              <HiOutlineLogout className="text-lg" /> Sign Out
                            </button>
                          </motion.div>
                        ) : (
                          <motion.div key="submenu" variants={menuVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }}>
                            <button onClick={() => setActiveMenu("main")} className="flex items-center gap-2 px-4 py-3 text-xs font-bold text-[#1976D2] hover:bg-blue-50 w-full rounded-xl mb-2">
                              <ChevronLeft size={16} /> BACK TO MENU
                            </button>
                            <div className="px-2">
                              {(activeMenu === "classes" ? classesLinks : activitiesLinks).map((sub, idx) => (
                                <Link key={idx} to={sub.path} onClick={closeAll} className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 rounded-xl">
                                  <span className="opacity-70">{sub.icon}</span> {sub.label}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <button 
                  onClick={() => navigate("/Loginrole")} 
                  className="px-6 py-2 text-sm font-bold text-[#1976D2] border border-[#1976D2] rounded-xl hover:bg-blue-50 transition-all"
                >
                  Login
                </button>
                <button 
                  onClick={() => navigate("/RoleSelection")} 
                  className="px-6 py-2.5 bg-[#1976D2] text-white text-sm font-bold rounded-xl shadow-md hover:bg-blue-700 transition-all"
                >
                  Get Started
                </button>
              </div>
            )}

            {/* Mobile Toggle Button */}
            <button 
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2.5 bg-slate-900 text-white rounded-xl shadow-lg z-[100] transition-transform active:scale-90"
            >
              {mobileOpen ? <HiX size={22} /> : <HiMenuAlt3 size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full-Screen Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[55] bg-white pt-28 px-6 md:hidden flex flex-col"
          >
            <div className="flex flex-col gap-6">
              
              {publicLinks.map((link, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    closeAll();
                    if (link.isPeerMate) {
                      navigateToPeerMate();
                    } else {
                      navigate(link.path);
                    }
                  }}
                  className={`text-4xl font-bold text-left tracking-tight ${isActive(link.path) ? "text-[#1976D2]" : "text-slate-900"}`}
                >
                  {link.label}
                </button>
              ))}
              
              <div className="h-[1px] bg-slate-100 my-4" />

              {!student ? (
                <div className="flex flex-col gap-4">
                  <motion.button 
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { navigate("/Loginrole"); closeAll(); }}
                    className="w-full py-4 text-center font-bold text-[#1976D2] border-2 border-[#1976D2] rounded-2xl active:bg-blue-50"
                  >
                    Login
                  </motion.button>
                  <motion.button 
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { navigate("/RoleSelection"); closeAll(); }}
                    className="w-full py-4 text-center font-bold text-white bg-[#1976D2] rounded-2xl shadow-xl shadow-blue-100 active:bg-blue-700"
                  >
                    Get Started
                  </motion.button>
                </div>
              ) : (
                <motion.button 
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { navigate("/dashboard"); closeAll(); }}
                  className="w-full py-4 text-center font-bold text-white bg-slate-900 rounded-2xl shadow-xl"
                >
                  Dashboard
                </motion.button>
              )}

              {student && (
                <div className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${planBadgeClass}`}>
                  <CheckCircle2 size={14} />
                  <span className="text-xs font-semibold">{planName}</span>
                </div>
              )}
            </div>

            <div className="mt-auto pb-10 text-center">
              <p className="text-slate-400 text-xs font-medium uppercase tracking-widest">© 2026 Peer2Learn</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}